import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Input, Pagination, Text } from "@zorni/components-lib";

import EstablishmentsLoadError from "../../common/establishmentsLoadError/EstablishmentsLoadError";
import EstablishmentTable from "./components/EstablishmentTable";

import { useAdmin } from "../../hooks/useAdmin";
import { ESTABLISHMENT_STATUS } from "../../common/model/enum";
import { useManageEstablishment } from "./hooks/useManageEstablishment";

import styles from "./ManageEstablishment.module.css";

const ManageEstablishment = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization } = useAdmin();

  const {
    establishments,
    establishmentsLoading,
    establishmentsRefreshing,
    error,
    refetch,
    currentPage,
    setCurrentPage,
    nbPages,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
  } = useManageEstablishment();

  const statusOptions = [
    { value: "all", label: "Tous" },
    { value: ESTABLISHMENT_STATUS.ACTIVATED, label: "Actif" },
    { value: ESTABLISHMENT_STATUS.MISSING_INFORMATIONS, label: "Incomplet" },
    { value: ESTABLISHMENT_STATUS.WAITING_FOR_APPROVAL, label: "En attente d'approbation" },
    { value: ESTABLISHMENT_STATUS.DISABLED, label: "Désactivé" },
  ];

  if (!organization) {
    return null;
  }

  // Page de la liste en échec : bloc « Réessayer » plutôt qu'une redirection
  // vers /error, l'incident est le plus souvent passager.
  if (error) {
    return <EstablishmentsLoadError onRetry={refetch} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.headerCard}>
        <div>
          <Text as="h3" variant="heading" size="lg" weight="semibold">
            {t("manageEstablishment.establishments")}
          </Text>
          <Text size="sm" color="subtle">
            Administration des établissements
          </Text>
        </div>

        <Button
          label={t("manageEstablishment.modal.addEstablishment")}
          variant="primary"
          iconName="iconPlus"
          iconPosition="left"
          onClick={() => navigate("/admin/establishments/add")}
        />
      </div>

      <div className={styles.card}>
        <div className={styles.filterBar}>
          <div className={styles.searchWrapper}>
            <Input
              inputSize="small"
              iconLeft="iconSearch"
              placeholder="Rechercher par nom..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className={styles.filtersRight}>
            <div className={styles.statusToggle}>
              {statusOptions.map((option) => (
                <Button
                  key={option.value}
                  size="small"
                  variant={statusFilter === option.value ? "primary" : "secondary"}
                  label={option.label}
                  onClick={() => setStatusFilter(option.value)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Pas de Preloader plein écran ici : il recouvrirait la page à chaque
            filtre. Premier chargement : indicateur inline ; ensuite la table
            reste affichée, grisée pendant la requête. */}
        {establishmentsLoading ? (
          <div className={styles.loading} role="status" aria-live="polite">
            <span className={styles.spinner} aria-hidden="true" />
            <Text size="sm" color="muted">
              Chargement des établissements…
            </Text>
          </div>
        ) : (
          <div
            className={establishmentsRefreshing ? styles.refreshing : undefined}
            aria-busy={establishmentsRefreshing}
          >
            <EstablishmentTable
              establishments={establishments}
              onDeleted={refetch}
              onOpenEstablishment={(establishmentId) => navigate(`${establishmentId}`)}
            />
          </div>
        )}

        <Pagination
          totalPages={nbPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default ManageEstablishment;
